import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateProductFamilyDto,
  ProductFamilyDto,
  UpdateProductFamilyDto,
} from '@services/dtos'
import { productFamilySchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid } from '@mui/material'
import {
  createProductFamily,
  updateProductFamily,
} from '@state/reducers/productFamilyReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import ColorPickerInput from '../inputs/ColorPickerInput'
import IconPickerInput from '../inputs/IconPickerInput'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { productFamilyService } from '@services/ProductFamilyService'
import { FormCrudHeader } from './FormCrudHeader'

type Props = {
  editingProductFamily?: ProductFamilyDto | null
  onClose: () => void
}

const ProductFamilyForm: React.FC<Props> = ({
  editingProductFamily,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors }
  } = useForm<CreateProductFamilyDto>({
    defaultValues: editingProductFamily
      ? {
          code: editingProductFamily.code,
          name: editingProductFamily.name,
          description: editingProductFamily.description,
          parentFamilyId: editingProductFamily.productFamily?.id,
          color: editingProductFamily.color,
          icon: editingProductFamily.icon,
        }
      : { color: '#000000', icon: 'Science' },
    resolver: yupResolver(productFamilySchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: UpdateProductFamilyDto) => {
    setIsSubmitting(true)

    if (editingProductFamily) {
      dispatch(updateProductFamily({ id: editingProductFamily.id, dto: 
          {
            ...data,
            parentFamilyId: data.parentFamilyId ?? null
          }
         }))
        .unwrap()
        .then(() => {
          cb('La famille de produits a été modifiée avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le code n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createProductFamily(data as CreateProductFamilyDto))
        .unwrap()
        .then(() => {
          cb('La famille de produits a été créée avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le code n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields = [
    { name: 'code', label: 'Code', sm: 6 },
    { name: 'name', label: 'Nom' , sm: 6},
    { name: 'description', label: 'Description', type: 'textarea', sm: 12 },
  ]
  
  const fetchProductFamilies = async (search: string) => {
    const page = await productFamilyService.getAll({ search })
    const list = page?.datas.filter(({ id }) => id !== editingProductFamily?.id) ?? []
    return list
  }

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingProductFamily}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Famille de produits
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {fields.map((field) => (
                <Grid item xs={12} sm={field.sm ? field.sm : 6} key={field.name}>
                  <InputField
                    control={control}
                    name={field.name}
                    label={field.label}
                    type={field.type ? field.type : 'text'}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 2 : undefined}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <ColorPickerInput control={control} name="color" label="Couleur" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <IconPickerInput 
                  control={control} 
                  name="icon" 
                  label="Icône"
                  icons={[
                    { name: 'Science', label: 'Chimie' },
                    { name: 'Biotech', label: 'Biotechnologie' },
                    { name: 'Medication', label: 'Médicament' },
                    { name: 'MedicationLiquid', label: 'Solution injectable' },
                    { name: 'Vaccines', label: 'Vaccin / injection' },
                    { name: 'LocalPharmacy', label: 'Pharmacie' },
                    { name: 'Bolt', label: 'Énergie / accélérateur' },
                    { name: 'Flare', label: 'Rayonnement' },
                    { name: 'Visibility', label: 'Imagerie' },
                    { name: 'CameraAlt', label: 'Caméra / gamma caméra' },
                    { name: 'Person', label: 'Patient' },
                    { name: 'AccessibilityNew', label: 'Soins au patient' },
                    { name: 'Healing', label: 'Guérison / traitement' },
                    { name: 'LocalShipping', label: 'Transport' },
                    { name: 'Inventory', label: 'Stock / kits' },
                    { name: 'Assignment', label: 'Ordonnance / dossier' },
                    { name: 'Verified', label: 'Contrôle qualité' },
                    { name: 'Shield', label: 'Sécurité / radioprotection' },
                  ]} />
              </Grid>

              <Grid item xs={12}>
                <ServerAutocomplete<ProductFamilyDto>
                  fetchService={fetchProductFamilies}
                  onChange={(value: ProductFamilyDto | null) => {
                    setValue('parentFamilyId', value?.id)
                  }}
                  getValue={(value) => value?.id?.toString() || null}
                  getOptionLabel={(value) => {
                    const productFamily = value as ProductFamilyDto
                    return productFamily ? productFamily.name : ''
                  }}
                  debounceMs={300}
                  placeholder="Rechercher une famille de produits"
                  label="Famille de produits"
                  helperText={errors.parentFamilyId?.message}
                  error={!!errors.parentFamilyId}
                  minSearchLength={0}
                  initialOptions={editingProductFamily?.productFamily ? [editingProductFamily.productFamily] : []}
                  value={editingProductFamily?.productFamily ?? null}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default ProductFamilyForm
